<template>
<div>
    <base-card overflowVisible>
        <h3 v-html="$t('medicationReminder')" class="text-center mb-4"></h3>
        <b-row class="justify-content-center mb-4">
            <b-col cols="12" xl="6">
                <base-select @search="searchMedicine($event)" searchable :loading="medicine.loading" :options="medicine.options" v-model="medicine.value" @option:selected="onMedicineSelected" label="name" valueProp="id" :placeholder="$t('enterMedicineName')"></base-select>
            </b-col>
        </b-row>
        <div v-if="medicine.value">
            <b-row class="justify-content-center mt-5">
                <b-col cols="12">
                    <h5 v-text="$t('reminderMedicationForm.frequencyTitle')"></h5>
                </b-col>
                <b-col class="text-center" cols="12">
                    <base-radio-group v-model="reminderFrequency.value" :options="reminderFrequency.options" @input="onReminderChange"></base-radio-group>
                </b-col>
            </b-row>
            <b-row v-if="reminderFrequency.value === INTERVAL_FREQUENCY_OPTION" class="justify-content-center row-gap">
                <b-col cols="12" md="3" xl="2">
                    <base-input v-model="reminderFrequency.interval.value" @input="updateIntervalEndHoursOptions" type="number" :min="1" :max="hoursIntervalSelected ? 23 : 90"></base-input>
                </b-col>
                <b-col class="mb-4" cols="12" md="3" xl="2">
                    <base-select v-model="reminderFrequency.interval.optionValue" :loading="false" :options="reminderFrequency.interval.options" label="name" valueProp="value" :placeholder="$t('reminderMedicationForm.option')"></base-select>
                </b-col>
            </b-row>
            <b-row v-if="reminderFrequency.value === SEVERAL_TIMES_DAY_FREQUENCY_OPTION" class="justify-content-center row-gap">
                <b-col cols="5" md="3" xl="2">
                    <base-input v-model="reminderFrequency.severalTimesDay.value" type="number" :min="2" :max="10" @input="onReminderChange"></base-input>
                </b-col>
                <span class="vertical-align" v-html="$t('reminderMedicationForm.tabletsDay')"></span>
            </b-row>
            <b-row v-if="reminderFrequency.value === WEEK_DAYS_FREQUENCY_OPTION" class="justify-content-center row-gap">
                <b-col cols="12">
                    <h5 v-text="$t('reminderMedicationForm.selectWeekDays')"></h5>
                </b-col>
                <b-col cols="12" class="text-center">
                    <b-form-checkbox-group class="form-group" v-model="reminderFrequency.weekDays.value" :options="reminderFrequency.weekDays.options" value-field="value" text-field="name"></b-form-checkbox-group>
                </b-col>
            </b-row>
            <b-row v-if="reminderFrequency.value === CYCLIC_FREQUENCY_OPTION" class="justify-content-center row-gap">
                <b-col cols="5" md="6" xl="2">
                    <base-input v-model="reminderFrequency.cyclic.tablets" type="number" :min="1"></base-input>
                </b-col>
                <span class="vertical-align" v-html="$t('reminderMedicationForm.intakes')"></span>
                <b-col cols="5" md="6" xl="2">
                    <base-input v-model="reminderFrequency.cyclic.pauses" type="number" :min="1"></base-input>
                </b-col>
                <span class="vertical-align" v-html="$t('reminderMedicationForm.pauses')"></span>
                <b-col cols="12">
                    <h5 v-text="$t('reminderMedicationForm.startDate')"></h5>
                </b-col>
                <b-col cols="12" xl="6">
                    <base-datepicker v-model="reminderFrequency.cyclic.startDate" dateAfterNow></base-datepicker>
                </b-col>
            </b-row>
            <b-row v-if="reminderFrequency.value === INTERVAL_FREQUENCY_OPTION" class="justify-content-center row-gap">
                <b-col cols="12" md="3" xl="2">
                    <span class="block-center" v-html="$t('reminderMedicationForm.tablets')"></span>
                    <base-input v-model="medicine.tabletsOptions[0].value" type="number" :min="1"></base-input>
                </b-col>
                <b-col cols="12" md="4" xl="2" class="mb-4">
                    <span class="block-center" v-html="$t('reminderMedicationForm.startHour')"></span>
                    <base-timepicker noMarginBottom buttonOnly v-model="medicine.tabletsOptions[0].startHour" @input="updateIntervalEndHoursOptions" :placeholder="`${$t('reminderMedicationForm.fromHour')}`"></base-timepicker>
                </b-col>
                <b-col v-if="hoursIntervalSelected" cols="12" md="3" xl="2" class="mb-4">
                    <span class="block-center" v-html="$t('reminderMedicationForm.endHour')"></span>
                    <base-select avoidDefaultSort noMarginBottom buttonOnly v-model="medicine.tabletsOptions[0].endHour" :options="reminderFrequency.interval.endHoursOptions" label="value" valueProp="value" :placeholder="`${$t('reminderMedicationForm.toHour')}`"></base-select>
                </b-col>
            </b-row>
            <div v-if="showTabletsSection">
                <b-row v-for="(item, index) in medicine.tabletsOptions" :key="`tablets${index}`" class="justify-content-center row-gap"> 
                    <b-col cols="12" md="4" xl="2">
                        <span class="block-center" v-html="$t('hour')"></span>
                        <base-timepicker noMarginBottom buttonOnly v-model="item.startHour" :placeholder="$t('reminderMedicationForm.hourTitle')"></base-timepicker>
                    </b-col>
                    <b-col cols="12" md="3" xl="2">
                        <span class="block-center" v-html="$t('reminderMedicationForm.tablets')"></span>
                        <base-input v-model="item.value" type="number" :min="1"></base-input>
                    </b-col>
                </b-row>
            </div>
            <b-row class="justify-content-center row-gap mt-5">
                <b-col cols="12">
                    <h5 v-text="$t('reminderMedicationForm.reminderFillQuestion')"></h5>
                </b-col>
                <b-col cols="12" class="text-center">
                    <binary-radio-group v-model="tabletsReminder.enabled" noMarginTop></binary-radio-group>
                </b-col>
            </b-row>
            <b-row v-if="tabletsReminder.enabled" class="justify-content-center row-gap">
                <b-col cols="12" md="3" xl="2">
                    <base-input v-model="tabletsReminder.left" type="number" :min="1"></base-input>
                </b-col>
                <span class="vertical-align" v-html="$t('reminderMedicationForm.reminderMedicineTitle')"></span>
                <b-col cols="12" md="3" xl="2">
                    <base-input v-model="tabletsReminder.limit" type="number" :min="0" :max="tabletsReminder.left ? tabletsReminder.left - 1 : null"></base-input>
                </b-col>
                <span class="vertical-align" v-html="$t('reminderMedicationForm.units')"></span>
            </b-row>
            <b-row class="justify-content-center row-gap mt-5">
                <b-col cols="12">
                    <h5 v-text="$t('reminderMedicationForm.notificationsQuestion')"></h5>
                </b-col>
                <div class="text-center">
                    <base-checkbox style="margin-right: 3rem;" class="inline-block" v-model="notifications.email">Email</base-checkbox>
                    <base-checkbox class="inline-block" v-model="notifications.whatsapp">Whatsapp</base-checkbox>
                </div>
            </b-row>
            <b-row class="justify-content-center mt-5">
                <b-col cols="12" xl="6">
                    <base-textarea v-model="notes" :placeholder="`${$t('notes')} ${$t('optional')}`"></base-textarea>
                </b-col>
            </b-row>
            <div class="text-center mt-5">
                <base-button @click="save">{{ $t('save-btn') }}</base-button> <base-button gray @click="$emit('onShowReminderMenu')">{{ $t('cancel-btn') }}</base-button>
            </div>
        </div>
    </base-card>
</div>
</template>

<script>
import BaseDatepicker from '@/components/base/forms/BaseDatepicker'
import BaseCheckbox from '@/components/base/forms/BaseCheckbox'
import BaseTextArea from '@/components/base/forms/BaseTextarea'
import clinicHistoryMixin from '@/mixins/clinicHistory.mixin'
import clinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import medicationReminderRepository from '@/repositories/MedicationReminderRepository'
import { MENU_PATH } from '@/constants/clinicHistory'
import BackBtn from '@/components/base/buttons/BackButton'
import BaseRadioGroup from '@/components/base/forms/BaseRadioGroup'
import BaseTimepicker from '@/components/base/forms/BaseTimepicker'
import { getWeekDaysNamesWithValue, getLocalHourFromDate, getDateFromDateAndHour, getCurrentDateFromHour } from '@/helpers/date.helpers'
import { BFormCheckboxGroup } from 'bootstrap-vue'
import BinaryRadioGroup from '@/components/base/forms/BinaryRadioGroup'
import { INTERVAL_FREQUENCY_OPTION, SEVERAL_TIMES_DAY_FREQUENCY_OPTION, WEEK_DAYS_FREQUENCY_OPTION, CYCLIC_FREQUENCY_OPTION, HOURS_INTERVAL_OPTION, DAYS_INTERVAL_OPTION } from '@/constants/medicationReminder'

const REMINDER_DEFAULT_VALUE = { value: 1, startHour: '06:00', endHour: '00:00' }

export default {
    components: {
        BaseDatepicker,
        BackBtn,
        BaseRadioGroup,
        BaseTimepicker,
        BFormCheckboxGroup,
        BinaryRadioGroup,
        BaseTextArea,
        BaseCheckbox
    },
    data () {
        return {
            notes: null,
            editing: false,
            notifications: {
                email: true,
                whatsapp: true
            },
            tabletsReminder: {
                total: null,
                left: null,
                limit: null,
                enabled: true
            },
            medicine: {
                value: null,
                tablets: null,
                tabletsOptions: [],
                options: [],
                loading: false
            },
            reminderFrequency: {
                options: [ { value: INTERVAL_FREQUENCY_OPTION, text: this.$t('reminderMedicationForm.frequency.interval') }, { value: SEVERAL_TIMES_DAY_FREQUENCY_OPTION, text: this.$t('reminderMedicationForm.frequency.severalTimesDay') }, { value: WEEK_DAYS_FREQUENCY_OPTION, text: this.$t('reminderMedicationForm.frequency.weekDays') }, { value: CYCLIC_FREQUENCY_OPTION, text: this.$t('reminderMedicationForm.frequency.cyclic') } ],
                interval: {
                    value: 6,
                    optionValue: HOURS_INTERVAL_OPTION,
                    options: [{ name: this.$t('hours'), value: HOURS_INTERVAL_OPTION }, { name: this.$t('days'), value: DAYS_INTERVAL_OPTION }],
                    endHoursOptions: []
                },
                severalTimesDay: {
                    value: 3
                },
                weekDays: {
                    value: [],
                    options: this.getWeekDaysNamesFormatted()
                },
                cyclic: {
                    tablets: 21,
                    pauses: 7,
                    startDate: null
                },
                value: null
            },
            MENU_PATH,
            INTERVAL_FREQUENCY_OPTION,
            SEVERAL_TIMES_DAY_FREQUENCY_OPTION,
            WEEK_DAYS_FREQUENCY_OPTION,
            CYCLIC_FREQUENCY_OPTION,
            HOURS_INTERVAL_OPTION
        }
    },
    mixins: [clinicHistoryMixin],
    props: {
        formData: Object,
        medicationCode: String
    },
    created () {
        window.scrollTo(0, 0)
        if (this.formData) this.loadFormData()
        if (this.medicationCode) {
            this.getMedicationByCode(this.medicationCode).then(response => {
                this.tabletsReminder.limit = parseInt(parseInt(this.tabletsReminder.total) / 4)
            })
        }
    },
    methods: {
        loadFormData () {
            this.editing = true
            this.getMedicationByCode(this.formData.medication.id)
            this.reminderFrequency.value = this.formData.reminderType.id
            this.tabletsReminder = this.formData.tabletsReminder
            this.notifications = this.formData.notifications
            this.notes = this.formData.notes
            if (this.formData.interval) this.loadIntervalData()
            if (this.formData.severalTimesWeek) this.loadSeveralTimesWeekData()
            if (this.formData.severalTimesDay) this.loadSeveralTimesDayData()
            if (this.formData.cyclic) this.loadCylicData()
        },
        loadIntervalData () {
            this.reminderFrequency.interval.value = this.formData.interval.hours || this.formData.interval.days
            this.reminderFrequency.interval.optionValue = this.formData.interval.hours ? HOURS_INTERVAL_OPTION : DAYS_INTERVAL_OPTION
            this.medicine.tabletsOptions.push({ value: this.formData.interval.tablets, startHour: getLocalHourFromDate(this.formData.interval.startDate), endHour: getLocalHourFromDate(this.formData.interval.endDate) })
            this.updateIntervalEndHoursOptions(true)
        },
        loadSeveralTimesWeekData () {
            this.reminderFrequency.weekDays.value = this.formData.severalTimesWeek.weekDaysValues
            this.medicine.tabletsOptions.push({ value: this.formData.severalTimesWeek.tablets, startHour: getLocalHourFromDate(this.formData.severalTimesWeek.startDate) })
        },
        loadSeveralTimesDayData () {
            this.reminderFrequency.severalTimesDay.value = this.formData.severalTimesDay.length
            this.medicine.tabletsOptions = this.formData.severalTimesDay.map(item => { return { value: item.tablets, startHour: getLocalHourFromDate(item.startDate) } })
        },
        loadCylicData () {
            this.formData.cyclic.cycle.startDate = new Date(this.formData.cyclic.cycle.startDate)
            this.reminderFrequency.cyclic = this.formData.cyclic.cycle
            this.medicine.tabletsOptions.push({ value: this.formData.cyclic.tablets, startHour: getLocalHourFromDate(this.formData.cyclic.startDate) })
        },
        onMedicineSelected () {
            let tablets = this.medicine.options.find(item => item.id === this.medicine.value).tablets
            this.tabletsReminder.left = tablets
            this.tabletsReminder.total = tablets
            this.tabletsReminder.limit = parseInt(parseInt(tablets) / 4)
        },
        getMedicationByCode (code) {
            return clinicHistoryRepository().getMedicinesById(code).then(response => {
                let selectedValue = response.data.data
                this.medicine.options = [selectedValue]
                this.medicine.value = selectedValue.id
                this.tabletsReminder.left = selectedValue.tablets
                this.tabletsReminder.total = selectedValue.tablets
            })
        },
        searchMedicine (search) {
            if (search) {
                clinicHistoryRepository().getMedicinesByName(search).then(response => {
                    this.medicine.options = response.data.data
                })
            }
        },
        save () {
            if (!this.medicine.value) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.medicine'))
            } else if (!this.reminderFrequency.value) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.frequencyOption'))
            } else if (!this.notifications.email && !this.notifications.whatsapp) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.notifications'))
            } else if (this.tabletsReminder.enabled && (this.tabletsReminder.limit === null || this.tabletsReminder.left === null)) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsReminderFill'))
            } else if (this.tabletsReminder.enabled && parseInt(this.tabletsReminder.limit) >= parseInt(this.tabletsReminder.left)) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsReminder'))
            } else if (this.reminderFrequency.value === INTERVAL_FREQUENCY_OPTION) {
                this.saveInterval()
            } else if (this.reminderFrequency.value === SEVERAL_TIMES_DAY_FREQUENCY_OPTION) {
                this.saveSeveralTimesDay()
            } else if (this.reminderFrequency.value === WEEK_DAYS_FREQUENCY_OPTION) {
                this.saveWeekDays()
            } else if (this.reminderFrequency.value === CYCLIC_FREQUENCY_OPTION) {
                this.saveCyclic()
            }
        },
        saveInterval () {
            if (this.reminderFrequency.interval.value < 1) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.interval'))
            } else if (this.medicine.tabletsOptions[0].value < 1) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsLessThan1'))
            } else if (!this.medicine.tabletsOptions[0].startHour) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsFromHour'))
            } else if (!this.medicine.tabletsOptions[0].endHour) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsToHour'))
            } else {
                let data = {
                    ...this.getCommomData(),
                    interval: {
                        hours: this.reminderFrequency.interval.optionValue === HOURS_INTERVAL_OPTION ? this.reminderFrequency.interval.value : null,
                        days: this.reminderFrequency.interval.optionValue === DAYS_INTERVAL_OPTION ? this.reminderFrequency.interval.value : null,
                        tablets: this.medicine.tabletsOptions[0].value,
                        startDate: getCurrentDateFromHour(this.medicine.tabletsOptions[0].startHour),
                        endDate: getCurrentDateFromHour(this.medicine.tabletsOptions[0].endHour)
                    }
                }
                this.saveReminderData(data)
            }
        },
        saveSeveralTimesDay () {
            if (this.reminderFrequency.severalTimesDay.value < 1) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.intakesDay'))
            } else if (this.medicine.tabletsOptions.some(item => item.value < 1)) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsLessThan1'))
            } else if (this.medicine.tabletsOptions.some(item => !item.startHour)) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsFromHour'))
            } else {
                let data = {
                    ...this.getCommomData(),
                    severalTimesDay: this.medicine.tabletsOptions.map(item => {
                        return { tablets: item.value, startDate: getCurrentDateFromHour(item.startHour) }
                    })
                }
                this.saveReminderData(data)
            }
        },
        saveWeekDays () {
            if (!this.reminderFrequency.weekDays.value.length) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.weekDays'))
            } else if (this.medicine.tabletsOptions[0].value < 1) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsLessThan1'))
            } else if (!this.medicine.tabletsOptions[0].startHour) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsFromHour'))
            } else {
                let data = {
                    ...this.getCommomData(),
                    severalTimesWeek: {
                        tablets: this.medicine.tabletsOptions[0].value,
                        startDate: getCurrentDateFromHour(this.medicine.tabletsOptions[0].startHour),
                        weekDaysValues: this.reminderFrequency.weekDays.value
                    }
                }
                this.saveReminderData(data)
            }
        },
        saveCyclic () {
            if (!this.reminderFrequency.cyclic.tablets) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsLessThan1'))
            } else if (!this.reminderFrequency.cyclic.startDate) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.cylicStartDate'))
            } else if (this.medicine.tabletsOptions[0].value < 1) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsLessThan1'))
            } else if (!this.medicine.tabletsOptions[0].startHour) {
                this.$store.dispatch('showDangerAlert', this.$t('reminderMedicationForm.error.tabletsFromHour'))
            } else {
                let cyclicData = {...this.reminderFrequency.cyclic}
                const startDateSplitted = typeof cyclicData.startDate === 'string' ? cyclicData.startDate.split('-').map(item => parseInt(item)) : cyclicData.startDate.toISOString().split('T')[0].split('-').map(item => parseInt(item))
                cyclicData.startDate = new Date(Date.UTC(startDateSplitted[0], startDateSplitted[1] - 1, startDateSplitted[2], 0, 0, 0))
                let data = {
                    ...this.getCommomData(),
                    cyclic: {
                        tablets: this.medicine.tabletsOptions[0].value,
                        startDate: getDateFromDateAndHour(cyclicData.startDate, this.medicine.tabletsOptions[0].startHour),
                        cycle: cyclicData
                    }
                }
                this.saveReminderData(data)
            }
        },
        getCommomData () {
            let medicine = this.medicine.options.find(item => item.id === this.medicine.value)
            return {
                medication: { id: medicine.id, name: medicine.name },
                reminderTypeId: this.reminderFrequency.value,
                tabletsReminder: this.tabletsReminder,
                notes: this.notes,
                notifications: this.notifications
            }
        },
        saveReminderData (data) {
            let responseEvent = response => {
                if (response.data) {
                    this.$emit('onShowReminderMenu')
                } else {
                    this.$store.dispatch('showDangerAlert', this.$t('defaultErrorMessage'))
                }
            }
            if (this.editing) {
                medicationReminderRepository.put(this.formData.id, data).then(response => {
                    responseEvent(response)
                })
            } else {
                medicationReminderRepository.post(data).then(response => {
                    responseEvent(response)
                })
            }
        },
        onReminderChange () {
            const defaultValue = REMINDER_DEFAULT_VALUE
            this.medicine.tabletsOptions = [defaultValue]
            if (this.reminderFrequency.value === SEVERAL_TIMES_DAY_FREQUENCY_OPTION) {
                let currentHour = defaultValue.startHour
                for (let i = 1; i < this.reminderFrequency.severalTimesDay.value; i++) {
                    currentHour = ((parseFloat(currentHour.replace(':', '.')) + 1) % 24).toFixed(2).replace('.', ':')
                    this.medicine.tabletsOptions.push({ value: 1, startHour: currentHour, endHour: '00:00' })
                }
            } else if (this.reminderFrequency.value === INTERVAL_FREQUENCY_OPTION) {
                this.updateIntervalEndHoursOptions()
            }
        },
        getWeekDaysNamesFormatted () {
            let weekDays = getWeekDaysNamesWithValue(true)
            weekDays.push(weekDays.shift())

            return weekDays
        },
        updateIntervalEndHoursOptions (editing) {
            if (!this.hoursIntervalSelected) return
            const startHour = parseFloat(this.medicine.tabletsOptions[0].startHour.replace(':', '.'))
            const hoursInterval = parseInt(this.reminderFrequency.interval.value)
            let loopsNumber = parseInt(24 / hoursInterval)
            let pivotHour = (startHour + hoursInterval) % 24
            let result = []
            for (let i = 0; i < loopsNumber; i++) {
                if (pivotHour !== startHour) result.push({ value: pivotHour.toFixed(2).replace('.', ':') })
                pivotHour = (pivotHour + hoursInterval) % 24
            }
            this.medicine.tabletsOptions[0].endHour = editing ? this.medicine.tabletsOptions[0].endHour : result[result.length - 1].value
            this.reminderFrequency.interval.endHoursOptions = result
        }
    },
    computed: {
        reminderIntervarlHours () {
            return [{ name: `6 ${this.$t('hours')}`, value: 6 }, { name: `8 ${this.$t('hours')}`, value: 8 }, { name: `12 ${this.$t('hours')}`, value: 12 }, { name: `24 ${this.$t('hours')}`, value: 24 }, { name: this.$t('other'), value: 0 }]
        },
        reminderIntervalAlternativeSource () {
            return [{ name: this.$t('hours'), value: 0 }, { name: this.$t('days'), value: 1 }]
        },
        showStartDate () {
            return this.reminderFrequency.value === CYCLIC_FREQUENCY_OPTION
        },
        showTabletsSection () {
            return this.reminderFrequency.value === SEVERAL_TIMES_DAY_FREQUENCY_OPTION || this.reminderFrequency.value === WEEK_DAYS_FREQUENCY_OPTION || this.reminderFrequency.value === CYCLIC_FREQUENCY_OPTION
        },
        hoursIntervalSelected () {
            return this.reminderFrequency.interval.optionValue === HOURS_INTERVAL_OPTION
        }
    }
}
</script>

<style lang="scss" scoped>
.row h5{
    text-align: center;
}
.text-center{
    text-align: center;
}
.row-gap{
    gap: 1rem
}
.vertical-align{
    display: inline-flex;
    align-items: center;
}
.inline-block{
    display: inline-block;
}
.block-center{
    display: block;
    text-align: center;
}
/deep/ .custom-checkbox{
    margin-top: 0;
}
</style>


// WEBPACK FOOTER //
// src/components/views/clinicHistory/medicationReminder/MedicationReminderForm.vue